import { BrowserRouter, Route, Routes, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AppShell } from "@/components/AppShell";

import LandingPage from "@/pages/Landing";
import LoginPage from "@/pages/Login";
// RegisterPage is intentionally NOT imported — /register redirects to /coming-soon
// while PUBLIC_ACCESS_LOCKED is on.
import AuthCallback from "@/pages/AuthCallback";
import ChangePasswordPage from "@/pages/ChangePassword";
import DashboardOverview from "@/pages/DashboardOverview";
import ModulePage from "@/pages/ModulePage";
import GuardianPage from "@/pages/Guardian";
import AuditLogPage from "@/pages/AuditLog";
import FinancePage from "@/pages/Finance";
import ShopifyPage from "@/pages/Shopify";
import SettingsPage from "@/pages/Settings";
import ShopPage from "@/pages/Shop";
import ShopProductPage from "@/pages/ShopProduct";
import ShopSuccessPage from "@/pages/ShopSuccess";
import ShopEbooksPage from "@/pages/ShopEbooks";
import ShopEbookDetailPage from "@/pages/ShopEbookDetail";
import EbookSuccessPage from "@/pages/EbookSuccess";
import LegalPage from "@/pages/LegalPage";
import ComingSoonPage from "@/pages/ComingSoon";
import AudiencePage from "@/pages/AudiencePage";
import WithdrawalPage from "@/pages/Withdrawal";
import ReferralLanding from "@/pages/ReferralLanding";
import GrowthStudioPage from "@/pages/GrowthStudio";
import GrowthAIPage from "@/pages/GrowthAI";
import CuratedPage from "@/pages/Curated";
import EbooksPage from "@/pages/Ebooks";

function Router() {
  const location = useLocation();
  // Detect Emergent OAuth callback synchronously (must run BEFORE ProtectedRoute).
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/shop" element={<ShopPage />} />
      <Route path="/shop/ebooks" element={<ShopEbooksPage />} />
      <Route path="/shop/ebooks/success" element={<EbookSuccessPage />} />
      <Route path="/shop/ebooks/:slug" element={<ShopEbookDetailPage />} />
      <Route path="/shop/success" element={<ShopSuccessPage />} />
      <Route path="/shop/:handle" element={<ShopProductPage />} />
      <Route path="/legal/:pageType" element={<LegalPage />} />
      <Route path="/shop/withdrawal" element={<WithdrawalPage />} />
      <Route path="/r/:code" element={<ReferralLanding />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<Navigate to="/coming-soon" replace />} />
      <Route path="/coming-soon" element={<ComingSoonPage />} />
      <Route
        path="/change-password"
        element={
          <ProtectedRoute>
            <ChangePasswordPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardOverview />} />
        <Route path="module/:moduleId" element={<ModulePage />} />
        <Route path="guardian" element={<GuardianPage />} />
        <Route path="audit" element={<AuditLogPage />} />
        <Route path="finance" element={<FinancePage />} />
        <Route path="shopify" element={<ShopifyPage />} />
        <Route path="growth" element={<GrowthStudioPage />} />
        <Route path="growth-ai" element={<GrowthAIPage />} />
        <Route path="curated" element={<Navigate to="/dashboard/module/product" replace />} />
        <Route path="ebooks" element={<EbooksPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <Router />
            <Toaster
              theme="dark"
              position="top-right"
              toastOptions={{
                style: {
                  background: "#0d0d0d",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "#f4f4f5",
                },
              }}
            />
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
